import Vue from 'vue';

/**
 * @callback Validator
 * @param {String} input
 * @returns {Boolean}
 */

/**
 * @typedef {Object} Validation
 * @property {String} message
 * @property {Validator} validation
 *
 * @typedef {(key: string, placeholders?: (any[] | Record<string, any>)) => string} LangFunction
 */

export default Vue.extend({
    props: {
        /**  @type {import('vue').PropOptions<LangFunction>} */
        lang: {
            type: Function,
            default(value) {
                return value;
            },
        },
        validate: {
            type: Boolean,
            default: false,
        },
        validateOnDirty: {
            type: Boolean,
            default : false,
        },
        /** @type {import('vue').PropOptions<Validation[]>} */
        customValidations: {
            type: Array,
            default: () => [],
            validator(validations) {
                if (validations.length === 0) {
                    return true;
                }

                return validations.every(validations => {
                    return (
                        typeof validations === 'object' &&
                        typeof validations.message === 'string' &&
                        typeof validations.validation === 'function'
                    );
                });
            },
        },
    },
    data() {
        return {
            /** @type {boolean} */
            blurredOnce: false,
            /** @type {string|null} */
            validationError: null,
            /** @type {string|null} */
            initialValue: null,
            dirty: false,
        };
    },
    created() {
        this.initialValue = this.inputText || '';
    },
    watch: {
        inputText(newValue) {
            if (this.validateOnDirty && newValue !== this.initialValue) {
                this.dirty = true;
            }
        },
    },
    computed: {
        /**
         * @returns {Record<string, Record<string, string>>}
         */
        errorMessages() {
            return {
                required: {
                    default: this.lang('webCommon.formError.required.default'),
                    password: this.lang('webCommon.formError.required.password'),
                },
                minlength: {
                    default: this.lang('webCommon.formError.minlength.default', [this.minlength]),
                    password: this.lang('webCommon.formError.minlength.password', [this.minlength]),
                },
                maxlength: {
                    default: this.lang('webCommon.formError.maxlength.default', [this.maxlength]),
                    password: this.lang('webCommon.formError.maxlength.password', [this.maxlength]),
                },
            };
        },

        /**
         * @returns {Record<string, Validation>}
         */
        predefinedValidations() {
            return {
                required: {
                    message: this.errorMessages.required[this.type] || this.errorMessages.required.default,
                    validation: input => input.trim().length > 0,
                },
                minlength: {
                    message: this.errorMessages.minlength[this.type] || this.errorMessages.minlength.default,
                    validation: input => !input || input.trim().length >= parseInt(this.minlength, 10),
                },
                maxlength: {
                    message: this.errorMessages.maxlength[this.type] || this.errorMessages.maxlength.default,
                    validation: input => !input || input.trim().length <= parseInt(this.maxlength, 10),
                },
            };
        },
        /**
         * @returns {Validation[]}
         */
        allValidations() {
            const validations = [...this.customValidations];

            if (this.required) {
                validations.push(this.predefinedValidations.required);
            }

            if (this.minlength !== null) {
                validations.push(this.predefinedValidations.minlength);
            }

            if (this.maxlength !== null) {
                validations.push(this.predefinedValidations.maxlength);
            }

            return validations;
        },
    },
    methods: {
        validationBlurHandler() {
            if (!this.validate) {
                return;
            }

            this.validationError = this.validateInputText();
            this.blurredOnce = true;
        },
        validationInputHandler() {
            if (!this.validate) {
                return;
            }

            const errorMessage = this.validateInputText();

            if (this.blurredOnce) {
                this.validationError = errorMessage;
            }
        },
        validateInputText() {
            const name = this.name;

            for (const { message, validation } of this.allValidations) {
                if (this.validateOnDirty && !this.dirty) {
                    continue;
                }

                if (!validation(this.inputText)) {
                    this.$emit('validation', false, name);
                    return message;
                }
            }

            this.$emit('validation', true, name);
            return null;
        },
    },
});
